import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase';
import RichText from '../components/RichText';
import BackButton from '../components/BackButton';

// SmartShopProducts - the SmartShop native Product CMS.
// Ports Glen's pilot-CMS design (catalogue.html) to React, against the live native
// backend. Self-contained styles (Glen's tokens) so it renders identically regardless
// of portal CSS. Colour accent is a CSS variable (--ss-accent) defaulting to Glen's red;
// per the two-axis rule (design = Glen's for everyone, colour = SmartBrand for everyone
// except Glen) this is where a tenant's SmartBrand colour would override later.
//
// MULTI-TENANT: nothing here may carry one shop's data. Categories are DERIVED from the
// active organisation's own catalogue (deriveCategories) - they were hardcoded to Glen's
// music taxonomy, which made every other shop mis-categorise from its first product.
//
// LANDMINE (D-036): list-products with NO gido_id param silently returns Glen's
// live 259-product catalogue. Every read MUST pass gido_id explicitly.
//
// Auth: Bearer <firebase id token>, plus gido_id in the body on writes (D-084).
// save-product takes the writable organisation as a verified parameter and proves the
// caller's seat with resolve_gidu - it no longer derives tenancy from the token, and
// denies an org the caller has no seat in. Sending gido_id is not a weakening: a seat
// holder has no gido_registry record to derive from at all (D-082).

// The AUTHENTICATED owner read, not the public storefront endpoint. list-products
// filters status == "active" AND published == true, so anything archived or unpublished
// disappeared from this CMS as well as from the shop - the Published toggle was a
// one-way door, switchable off and never back on. list-catalogue returns every status
// for the verified organisation, which is what a read-modify-write CMS has to see.
// The public endpoint is untouched; no storefront behaviour changes.
const CATALOGUE_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartshop-list-catalogue';
const SAVE_URL   = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartshop-save-product';
// The structure write. Same gate as save-product: Bearer portal token plus
// gido_id in the body as a verified parameter (D-084) - it imports save-product's
// own _authorize rather than carrying a second copy of the auth block.
const SAVE_TAXONOMY_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartshop-save-taxonomy';
const UPLOAD_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartshop-upload-image';

const STOCK_STATUSES = [
  ['in_stock', 'In Stock'],
  ['low_stock', 'Low Stock'],
  ['out_of_stock', 'Out of Stock'],
  ['pre_order', 'Pre-Order'],
];
const PRICE_TYPES = ['fixed', 'variable', 'enquire'];

// Categories are DERIVED from the tenant's own catalogue, never hardcoded. A shared
// multi-tenant CMS carrying one shop's taxonomy (these were Glen's music categories)
// means every other shop mis-categorises from day one. A new shop's categories appear
// the moment its products exist - no code change, ever.
function deriveCategories(products) {
  return [...new Set((products || []).map((p) => p.category).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
}

// ── Glen's design tokens, verbatim ──────────────────────────────────────────
const C = {
  bg: '#0a0c0a', bg2: '#0d0f0d', bg3: '#111311', bg4: '#1a1c1a',
  text: '#F4F1E1', accent: 'var(--ss-accent, #8B1A1A)', success: '#4a9e4a',
  line: 'rgba(255,255,255,0.08)', line2: 'rgba(255,255,255,0.06)',
  faint: 'rgba(255,255,255,0.3)', faint2: 'rgba(255,255,255,0.4)',
};

const S = {
  page: { background: C.bg, color: C.text, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' },
  header: { background: C.bg, borderBottom: `1px solid ${C.line}`, padding: '0 28px', height: 56, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 },
  h1: { color: C.accent, fontSize: 16, fontStyle: 'italic', fontWeight: 900, fontFamily: 'Georgia, serif' },
  tabs: { display: 'flex', borderBottom: `1px solid ${C.line}`, background: C.bg2 },
  tab: (active) => ({ padding: '14px 28px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', cursor: 'pointer', borderBottom: `2px solid ${active ? C.accent : 'transparent'}`, color: active ? C.text : C.faint2, background: 'none', border: 'none', borderBottom: `2px solid ${active ? C.accent : 'transparent'}` }),
  toolbar: { padding: '14px 28px', display: 'flex', gap: 12, alignItems: 'center', borderBottom: `1px solid ${C.line2}`, background: C.bg2, flexWrap: 'wrap' },
  search: { background: C.bg4, border: `1px solid rgba(255,255,255,0.1)`, color: C.text, padding: '8px 14px', fontSize: 12, outline: 'none', width: 240 },
  select: { background: C.bg4, border: `1px solid rgba(255,255,255,0.1)`, color: C.text, padding: '8px 14px', fontSize: 12, outline: 'none', cursor: 'pointer' },
  countBadge: { fontSize: 10, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.1em', marginLeft: 'auto' },
  content: { padding: '20px 28px' },
  row: { background: C.bg3, border: `1px solid ${C.line2}`, marginBottom: 8 },
  rowHead: { display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', cursor: 'pointer' },
  thumb: { width: 52, height: 52, objectFit: 'cover', flexShrink: 0, background: C.bg4 },
  rowName: { fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowCat: { fontSize: 10, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 },
  rowPrice: { fontSize: 14, fontWeight: 900, color: C.accent, flexShrink: 0 },
  badge: (kind) => ({
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '3px 8px', flexShrink: 0,
    ...(kind === 'live'   ? { background: 'rgba(45,158,45,0.15)', color: C.success, border: '1px solid rgba(45,158,45,0.3)' } : {}),
    ...(kind === 'hidden' ? { background: 'rgba(255,255,255,0.05)', color: C.faint, border: '1px solid rgba(255,255,255,0.1)' } : {}),
    ...(kind === 'archived' ? { background: 'rgba(192,39,45,0.12)', color: '#d8737a', border: '1px solid rgba(192,39,45,0.35)' } : {}),
  }),
  panel: { borderTop: `1px solid ${C.line2}`, padding: 20, display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 28 },
  imgTitle: { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.faint2, fontWeight: 700 },
  imgGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 },
  imgItem: (main) => ({ position: 'relative', aspectRatio: '1', background: C.bg4, overflow: 'hidden', border: `2px solid ${main ? C.accent : 'transparent'}` }),
  imgTag: { width: '100%', height: '100%', objectFit: 'cover' },
  mainBadge: { position: 'absolute', top: 4, left: 4, background: C.accent, color: 'white', fontSize: 8, fontWeight: 700, padding: '2px 5px', textTransform: 'uppercase' },
  uploadZone: { border: '2px dashed rgba(255,255,255,0.15)', padding: 20, textAlign: 'center', cursor: 'pointer', fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.08em' },
  fields: { display: 'flex', flexDirection: 'column', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.35)', fontWeight: 700, display: 'flex', justifyContent: 'space-between' },
  input: { background: C.bg4, border: '1px solid rgba(255,255,255,0.1)', color: C.text, padding: '9px 12px', fontSize: 12, fontFamily: 'inherit', outline: 'none', width: '100%' },
  textarea: { background: C.bg4, border: '1px solid rgba(255,255,255,0.1)', color: C.text, padding: '9px 12px', fontSize: 12, fontFamily: 'inherit', outline: 'none', width: '100%', resize: 'vertical', minHeight: 72, lineHeight: 1.5 },
  togglesRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 },
  toggleItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: C.bg4, border: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer' },
  toggleLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.6)' },
  toggleSw: (on) => ({ width: 32, height: 18, background: on ? C.accent : 'rgba(255,255,255,0.1)', borderRadius: 9, position: 'relative', flexShrink: 0, transition: 'background 0.2s' }),
  toggleKnob: (on) => ({ content: '', position: 'absolute', width: 12, height: 12, background: 'white', borderRadius: '50%', top: 3, left: on ? 17 : 3, transition: 'left 0.2s' }),
  actions: { display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' },
  btn: (kind) => ({
    border: 'none', padding: '10px 18px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer',
    ...(kind === 'save'   ? { background: C.accent, color: 'white' } : {}),
    ...(kind === 'ghost'  ? { background: 'rgba(255,255,255,0.05)', color: C.faint2, border: '1px solid rgba(255,255,255,0.1)' } : {}),
    ...(kind === 'danger' ? { background: 'transparent', color: 'rgba(139,26,26,0.7)', border: '1px solid rgba(139,26,26,0.3)' } : {}),
  }),
  statusBar: (kind) => ({ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '10px 28px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: kind === 'error' ? C.accent : kind === 'success' ? C.success : C.faint, background: '#080a08', borderTop: `1px solid ${C.line2}`, textAlign: 'center', zIndex: 200 }),
  vRow: { display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 0.5fr', gap: 6, marginBottom: 6, alignItems: 'center' },
  vInput: { background: C.bg4, border: '1px solid rgba(255,255,255,0.1)', color: C.text, padding: '6px 8px', fontSize: 11, outline: 'none', width: '100%' },
  // A variant row carries its own picture, so it is wider than the options row and
  // keeps its own grid. Thumb, name, price, stock, visible, image, remove.
  varRow: { display: 'grid', gridTemplateColumns: '44px 1.3fr 0.8fr 0.9fr auto auto auto', gap: 6, marginBottom: 6, alignItems: 'center' },
  varThumb: { width: 44, height: 44, objectFit: 'cover', background: C.bg4, border: '1px solid rgba(255,255,255,0.08)', display: 'block' },
  varThumbEmpty: { width: 44, height: 44, background: C.bg4, border: '1px dashed rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: C.faint, textAlign: 'center', lineHeight: 1.1 },
  axisRow: { display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 6, marginBottom: 6, alignItems: 'center' },
  miniBtn: { background: 'rgba(255,255,255,0.05)', color: C.faint2, border: '1px solid rgba(255,255,255,0.1)', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 9px', cursor: 'pointer', whiteSpace: 'nowrap' },
};

// ── variant axes ────────────────────────────────────────────────────────────
// An axis is a kind of choice (Colour, Size); a variant is where the axes meet and is
// the only thing actually purchasable (D-048). Axes are DERIVED from the variants' own
// attributes dict and never stored separately, so there is no second source of truth
// about what a product's choices are, nothing new is persisted, and every existing
// product keeps working untouched.
function deriveAxes(variants) {
  const order = [];
  const seen = {};
  (variants || []).forEach((v) => {
    Object.entries(v.attributes || {}).forEach(([k, val]) => {
      if (!seen[k]) { seen[k] = []; order.push(k); }
      if (val && !seen[k].includes(val)) seen[k].push(val);
    });
  });
  return order.map((k) => ({ name: k, values: seen[k], values_raw: seen[k].join(', ') }));
}

function axisValues(axis) {
  if (axis.values_raw != null) {
    return [...new Set(axis.values_raw.split(',').map((s) => s.trim()).filter(Boolean))];
  }
  return axis.values || [];
}

// Every combination of every axis: one axis of 9 gives 9, two axes of 9 and 2 give 18.
function cartesian(axesList) {
  return axesList.reduce(
    (acc, axis) => acc.flatMap((combo) => axis.values.map((v) => ({ ...combo, [axis.name]: v }))),
    [{}],
  );
}

function sameAttributes(a, b) {
  const x = a || {}, y = b || {};
  const kx = Object.keys(x), ky = Object.keys(y);
  return kx.length === ky.length && kx.every((k) => x[k] === y[k]);
}

// Written for the person who will actually read it: a shop owner, someone covering a
// Saturday shift, a parent helping out. Not for an architect.
const VARIANT_HELP =
`A variant is one thing you can actually sell. A ukulele in blue is one variant, the same ukulele in red is another. Each one has its own stock, its own photo, and its own price if it differs from the product price.

Leave the price blank and the variant costs whatever the product costs. Fill it in only when that one is different.

If a choice does NOT change what you are selling - a free plectrum, a gift message - that is an Option further down, not a variant.`;

const AXIS_HELP =
`Use this when the same item sells in more than one way. Type what the choice is called - Colour, Size, Length - then list the possibilities separated by commas.

Generate then creates one row for every combination. Nine colours makes nine rows. Nine colours and two sizes makes eighteen, because blue-in-concert and blue-in-soprano run out separately and are two different things to sell.

Generate only ever ADDS. Rows you already filled in with prices, stock or photos are never changed or removed. Deleting a row is always your decision, with the × button.`;

// ── auth-carrying fetch helpers ─────────────────────────────────────────────
async function authFetch(url, opts = {}) {
  const token = await auth.currentUser.getIdToken();
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } });
}

async function saveProduct(body) {
  const res = await authFetch(SAVE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body), // body carries gido_id (D-084) - server verifies the seat
  });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

async function saveTaxonomy(body) {
  const res = await authFetch(SAVE_TAXONOMY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body), // body carries gido_id (D-084) - server verifies the seat
  });
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

// upload one file -> returns public_url. product_slug + image_slug + gido_id required.
// gido_id is the D-084 verified parameter: the endpoint proves the caller holds a seat
// in that org before writing. One login can hold seats in several orgs, so there is no
// single derivable answer - the active org must be named.
// XMLHttpRequest, not fetch: fetch cannot report upload progress - there is no event
// for bytes sent. XHR exposes upload.onprogress, which is the only way to show a shop
// owner that a 4 MB photo is actually moving rather than hung. Auth and the D-084
// gido_id parameter are carried exactly as authFetch did.
// An image in the form is either a plain url string - what list_products returns, and
// what legacy records hold - or a renditions object {status, small, medium, source_ref},
// which is what a new upload now returns and what migrated records carry. Everything
// that renders, keys or compares an image goes through these two helpers so both shapes
// behave identically. Mirrors _img_url in list_products and image_url in
// catalogue_source: one place that resolves the shape.
const imgUrl = (v) =>
  (typeof v === 'string' ? v : (v && (v.medium || v.small || v.source_ref)) || '');
const sameImg = (a, b) => !!imgUrl(a) && imgUrl(a) === imgUrl(b);

// Chips that sit ON TOP of a photo, so neither may inherit a translucent button style:
// a shot with a pale bottom-left corner swallowed the white "Main", and red-on-photo was
// legible only sometimes. Each chip is fully opaque and carries its own contrast - white
// with dark text to promote, solid red with white text to remove - so readability never
// depends on the picture underneath.
const CHIP_BASE = {
  padding: '5px 10px', fontSize: 11, fontWeight: 700, lineHeight: 1.1,
  border: '1px solid rgba(0,0,0,0.35)', borderRadius: 4, cursor: 'pointer',
  boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
};
const CHIP_MAIN = { ...CHIP_BASE, background: '#ffffff', color: '#111111' };

// The asset id, read off the stored filename - "korala-ukulele-1791450519728" for a
// slug-addressed upload, or a hash like "133331190d2342f0" for a deduplicated _assets/
// image. Shown on the card so an image can be named out loud, as the pilot CMS did.
// NOT a GID in the D-033 sense: these are storage asset ids, nothing to do with the
// registry. The rendition suffix is stripped so the same picture reads the same whether
// its small or medium copy is resolved.
const assetId = (v) => {
  const file = (imgUrl(v).split('?')[0].split('/').pop() || '');
  return file.replace(/\.(jpg|jpeg|png|webp|avif)$/i, '').replace(/__(small|medium|thumb)$/i, '');
};
const CHIP_REMOVE = { ...CHIP_BASE, background: '#c0272d', color: '#ffffff' };

function uploadImage(file, productSlug, imageSlug, gidoId, onProgress) {
  return auth.currentUser.getIdToken().then((token) => new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('product_slug', productSlug);
    fd.append('image_slug', imageSlug);
    fd.append('gido_id', gidoId);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', UPLOAD_URL);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      };
      // bytes are away; the server is still resizing and writing. Hold at 100.
      xhr.upload.onload = () => onProgress(100);
    }
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch (_) { /* non-JSON error body */ }
      if (xhr.status >= 200 && xhr.status < 300 && data.ok) resolve(data.renditions || data.public_url);
      else reject(new Error(data.reason || `upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('network error during upload'));
    xhr.onabort = () => reject(new Error('upload cancelled'));
    xhr.send(fd);
  }));
}

// Upload progress drawn as the DBOS mark filling bottom-to-top.
// Two copies of the same artwork: a dim greyscale one for the empty state, and the
// full-colour one revealed from below by a wrapper whose height IS the progress.
// Snapped to 10% steps so it reads as deliberate rather than jittery.
const DBOS_MARK = '/android-chrome-192x192.png';

function UploadMark({ pct, size = 54, label }) {
  const step = Math.min(100, Math.max(0, Math.round((pct || 0) / 10) * 10));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <img src={DBOS_MARK} alt="" width={size} height={size}
          style={{ position: 'absolute', left: 0, top: 0, opacity: 0.16, filter: 'grayscale(1)' }} />
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${step}%`,
          overflow: 'hidden', transition: 'height .35s cubic-bezier(.4,0,.2,1)' }}>
          <img src={DBOS_MARK} alt="" width={size} height={size}
            style={{ position: 'absolute', left: 0, bottom: 0 }} />
        </div>
      </div>
      <span style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.faint }}>
        {label || (step >= 100 ? 'Processing' : `${step}%`)}
      </span>
    </div>
  );
}

const DENY = {
  bad_token: 'Session token rejected - sign out and back in.',
  no_access: 'You do not have a seat in this organisation.',
  twofa_required: 'Two-factor authentication is required for this session.',
  no_smartshop_grant: 'This account is not licensed for SmartShop writes.',
  gido_id_required: 'No organisation selected.',
  // legacy reasons, pre-D-084 - kept so an un-migrated endpoint still reads clearly
  no_registry_record: 'No identity record for this account.',
  no_gido_on_record: 'This account has no GIDO on record.',
  twofa_not_enabled: 'Enable two-factor authentication to save.',
};

function slugify(s) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// ── form model ──────────────────────────────────────────────────────────────
function emptyForm() {
  return {
    product_id: '', slug: '', name: '', category: '', description: '', technical_specs: '',
    price_euros: '', stock_status: 'in_stock', price_type: 'fixed',
    status: 'active',
    featured: false, published: true, large_box: false, hygiene_sensitive: false,
    images: { primary: '', secondary: '', all: [] },
    variants: [], options: [],
  };
}

function productToForm(p) {
  // MUST match how the storefront builds its gallery: ShopPage, ProductPage and App all
  // use [primary_image, secondary_image, ...all_assets] deduped. Reading all_assets
  // alone showed 8 images in the CMS where the shop showed 10 - the same class of bug as
  // the Korala flip, where two lists of the same pictures were built from different
  // fields. Change one, change both.
  const all = [p.primary_image, p.secondary_image, ...(p.all_assets || [])]
    .filter((v, i, a) => v && a.indexOf(v) === i);
  return {
    product_id: p.product_id || '', slug: p.slug || '', name: p.name || '',
    category: p.category || '', description: p.description || '', technical_specs: p.technical_specs || '',
    price_euros: p.price != null ? String(p.price) : '',
    stock_status: p.stock_status || 'in_stock', price_type: p.price_type || 'fixed',
    // Carried explicitly. save-product defaults status to "active" when the body omits
    // it, so any save that does not send it silently republishes an archived product.
    status: p.status || 'active',
    featured: Boolean(p.featured), published: Boolean(p.published),
    large_box: Boolean(p.large_box), hygiene_sensitive: Boolean(p.hygiene_sensitive),
    images: { primary: p.primary_image || '', secondary: p.secondary_image || '', all },
    variants: p.variants || [], options: p.options || [],
  };
}

// ── small UI pieces ───────────────────────────────────────────────────────
function Toggle({ label, on, onClick }) {
  return (
    <div style={S.toggleItem} onClick={onClick}>
      <span style={S.toggleLabel}>{label}</span>
      <div style={S.toggleSw(on)}><div style={S.toggleKnob(on)} /></div>
    </div>
  );
}

// hint: an optional (i) that reveals an explanation inline. Any field can carry one -
// the next confusing control costs a string, not new markup. Click to toggle rather
// than hover, so it works on touch.
function Field({ label, children, hint }) {
  const [showHint, setShowHint] = useState(false);
  return (
    <div style={S.field}>
      <label style={S.label}>
        <span>{label}</span>
        {hint && (
          <button type="button" onClick={() => setShowHint((v) => !v)}
            title="What is this?"
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.25)',
              color: 'rgba(255,255,255,0.55)', borderRadius: '50%', width: 15, height: 15,
              fontSize: 9, lineHeight: '13px', cursor: 'pointer', padding: 0,
              fontWeight: 700, fontStyle: 'italic', flexShrink: 0,
            }}>i</button>
        )}
      </label>
      {hint && showHint && (
        <div style={{
          fontSize: 11, lineHeight: 1.6, color: 'rgba(255,255,255,0.6)',
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
          padding: '8px 10px', marginBottom: 6, whiteSpace: 'pre-line',
        }}>{hint}</div>
      )}
      {children}
    </div>
  );
}

// ── the edit / create form ──────────────────────────────────────────────────
function ProductForm({ initial, onSaved, onCancel, setStatus, activeGido, categories }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [deny, setDeny] = useState(null);
  const [pending, setPending] = useState([]); // File[] queued to upload on save (new products)
  const [upload, setUpload] = useState(null); // { pct, index, total } while an upload is in flight
  const [newCat, setNewCat] = useState(null);  // null = closed; '' or text = the add row is open
  const fileRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isCreate = !form.product_id;

  // ---- images ----
  function makeMain(img) {
    setForm((f) => {
      const all = [img, ...f.images.all.filter((a) => !sameImg(a, img))];
      return { ...f, images: { ...f.images, primary: img, all } };
    });
  }
  function removeImage(img) {
    setForm((f) => {
      const all = f.images.all.filter((a) => !sameImg(a, img));
      return { ...f, images: { primary: all[0] || '', secondary: all[1] || '', all } };
    });
  }

  async function onPickFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (isCreate) {
      // no slug yet - queue, upload on save
      setPending((p) => [...p, ...files]);
      setStatus(`${files.length} image(s) queued - will upload on save`);
      return;
    }
    // existing product: upload now against its slug
    setStatus(`Uploading ${files.length} image(s)...`);
    try {
      const urls = [];
      for (const file of files) {
        setUpload({ pct: 0, index: urls.length + 1, total: files.length });
        const url = await uploadImage(file, form.slug, `${slugify(form.name)}-${Date.now()}`, activeGido,
          (pct) => setUpload((u) => (u ? { ...u, pct } : u)));
        urls.push(url);
      }
      setUpload(null);
      setForm((f) => {
        const all = [...f.images.all, ...urls];
        return { ...f, images: { primary: f.images.primary || all[0] || '', secondary: f.images.secondary, all } };
      });
      setStatus('Images uploaded', 'success');
    } catch (err) {
      setUpload(null);
      setStatus(`Image upload failed: ${err.message}`, 'error');
    }
  }

  // ---- variants ----
  function addVariant() {
    setForm((f) => ({ ...f, variants: [...f.variants, { name: '', price_cents: null, stock_status: 'in_stock', attributes: {}, image: null, published: true }] }));
  }
  function setVariant(i, k, v) {
    setForm((f) => { const vs = [...f.variants]; vs[i] = { ...vs[i], [k]: v }; return { ...f, variants: vs }; });
  }
  function removeVariant(i) {
    setForm((f) => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }));
  }

  // ---- axes (the kinds of choice a variant is made of) ----
  const [axes, setAxes] = useState(() => deriveAxes(initial.variants));
  const [vUpload, setVUpload] = useState(null);   // { index, pct } while a variant image uploads
  const vFileRef = useRef();
  const vTargetRef = useRef(null);                // which variant row the file picker is for

  // Re-derive when the form is pointed at a different product.
    // Re-derive only when the form is pointed at a different product, never on every
  // variant edit - otherwise typing in a value box would rewrite the axis list underneath.
  useEffect(() => { setAxes(deriveAxes(form.variants)); }, [form.product_id]);

  function addAxis() { setAxes((a) => [...a, { name: '', values: [], values_raw: '' }]); }
  function setAxis(i, k, v) { setAxes((a) => { const n = [...a]; n[i] = { ...n[i], [k]: v }; return n; }); }
  function removeAxis(i) { setAxes((a) => a.filter((_, j) => j !== i)); }

  // Generate ADDS the missing combinations and touches nothing that already exists.
  // D-108's rule one level down: automation never removes what a human filled in,
  // because those rows carry prices, stock and photos somebody typed.
  function generateVariants() {
    const usable = axes
      .map((a) => ({ name: (a.name || '').trim(), values: axisValues(a) }))
      .filter((a) => a.name && a.values.length);
    if (!usable.length) { setStatus('Name at least one choice and list its values first', 'error'); return; }
    const combos = cartesian(usable);
    const existing = form.variants || [];
    const fresh = combos
      .filter((c) => !existing.some((v) => sameAttributes(v.attributes, c)))
      .map((c) => ({
        name: Object.values(c).join(' / '),
        price_cents: null,
        stock_status: 'in_stock',
        attributes: c,
        image: null,
        published: true,
      }));
    if (!fresh.length) { setStatus('Every combination already exists', 'success'); return; }
    setForm((f) => ({ ...f, variants: [...f.variants, ...fresh] }));
    setStatus(`${fresh.length} variant(s) added`, 'success');
  }

  // ---- variant image ----
  // The same upload path as product images, so the file lands in the datalake and the
  // Asset Library exactly like any other image - one ingestion route, no duplicate.
  // One image per variant (D-048); the storefront puts it first in the gallery when
  // that variant is selected, so it needs no caption - the variant's own name is it.
  //
  // Coerced through imgUrl(): uploadImage resolves `data.renditions || data.public_url`,
  // so it can return an OBJECT, and save-product keeps a variant image only when it is
  // a string. An object would be silently dropped to null on save, with no error.
  function pickVariantImage(i) {
    if (!form.slug) { setStatus('Save the product once, then add variant images', 'error'); return; }
    vTargetRef.current = i;
    vFileRef.current.value = '';
    vFileRef.current.click();
  }

  async function onPickVariantFile(e) {
    const file = (e.target.files || [])[0];
    const i = vTargetRef.current;
    if (!file || i == null) return;
    try {
      setVUpload({ index: i, pct: 0 });
      const res = await uploadImage(
        file, form.slug, `${slugify(form.name)}-v${i}-${Date.now()}`, activeGido,
        (pct) => setVUpload((u) => (u ? { ...u, pct } : u)),
      );
      setVariant(i, 'image', imgUrl(res) || null);
      setVUpload(null);
      setStatus('Variant image uploaded', 'success');
    } catch (err) {
      setVUpload(null);
      setStatus(`Variant image upload failed: ${err.message}`, 'error');
    }
  }

  // ---- options ----
  function addOption() {
    setForm((f) => ({ ...f, options: [...f.options, { name: '', type: 'select', required: false, choices: [] }] }));
  }
  function setOption(i, k, v) {
    setForm((f) => { const os = [...f.options]; os[i] = { ...os[i], [k]: v }; return { ...f, options: os }; });
  }
  function removeOption(i) {
    setForm((f) => ({ ...f, options: f.options.filter((_, j) => j !== i) }));
  }

  function buildBody(images, statusOverride) {
    const body = {
      gido_id: activeGido,           // D-084 - verified server-side by resolve_gidu
      name: form.name, category: form.category, description: form.description,
      technical_specs: form.technical_specs, price_euros: parseFloat(form.price_euros) || 0,
      stock_status: form.stock_status, price_type: form.price_type,
      status: statusOverride || form.status || 'active',   // never omit - save-product defaults it to "active"
      featured: form.featured, published: form.published,
      large_box: form.large_box, hygiene_sensitive: form.hygiene_sensitive,
      images, variants: form.variants,
      options: form.options.map((o) => ({
        name: o.name, type: o.type || 'select', required: !!o.required,
        choices: o.choices_raw != null
          ? o.choices_raw.split(',').map((s) => s.trim()).filter(Boolean)
          : (o.choices || []),
      })),
    };
    if (form.product_id) body.product_id = form.product_id;
    if (form.slug) body.slug = form.slug;
    return body;
  }

  // statusOverride mirrors publishOverride. The footer Archive and Restore buttons go
  // through this same save, so the image upload-then-attach handling comes along
  // unchanged instead of being reimplemented in a second place.
  async function save(publishOverride, statusOverride) {
    setSaving(true); setDeny(null);
    const pub = publishOverride == null ? form.published : publishOverride;
    try {
      // 1. create/update to get a product_id + slug
      let images = form.images;
      const firstBody = buildBody(images, statusOverride);
      firstBody.published = pub;
      const { res, data } = await saveProduct(firstBody);
      if (!res.ok || !data.ok) {
        if (res.status === 401 || res.status === 403) setDeny(data.reason || 'denied');
        else setStatus(`Save failed: ${data.reason || res.status}`, 'error');
        setSaving(false);
        return;
      }
      const savedSlug = data.product?.slug || form.slug;
      const savedId = data.product_id;

      // 2. if we queued images on create, upload them now against the real slug, then attach
      if (pending.length) {
        setStatus(`Uploading ${pending.length} image(s)...`);
        const urls = [];
        for (const file of pending) {
          setUpload({ pct: 0, index: urls.length + 1, total: pending.length });
          const url = await uploadImage(file, savedSlug, `${slugify(form.name)}-${Date.now()}-${urls.length}`, activeGido,
            (pct) => setUpload((u) => (u ? { ...u, pct } : u)));
          urls.push(url);
        }
        setUpload(null);
        const all = [...images.all, ...urls];
        const withImages = { primary: images.primary || all[0] || '', secondary: images.secondary || all[1] || '', all };
        // resend FULL body + product_id + images (save-product requires name+category every write)
        const attachBody = buildBody(withImages, statusOverride);
        attachBody.product_id = savedId;
        attachBody.slug = savedSlug;
        attachBody.published = pub;
        const r2 = await saveProduct(attachBody);
        if (!r2.res.ok || !r2.data.ok) { setStatus('Product saved, image attach failed', 'error'); setSaving(false); onSaved(savedId); return; }
      }
      setStatus('Saved', 'success');
      onSaved(savedId);
    } catch (err) {
      setUpload(null);
      setStatus(`Network error: ${err.message}`, 'error');
    }
    setSaving(false);
  }

  const images = form.images.all.length ? form.images.all : [form.images.primary].filter(Boolean);

  return (
    <div style={S.panel}>
      {/* LEFT: images */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={S.imgTitle}>Images ({images.length + pending.length})</p>
        <div style={S.imgGrid}>
          {images.map((img, i) => (
            <div key={imgUrl(img) + i} style={S.imgItem(sameImg(img, form.images.primary))}>
              {sameImg(img, form.images.primary) && (
                <div style={{ ...S.mainBadge, background: '#111111', color: '#ffffff', padding: '2px 8px', borderRadius: 4 }}>Main</div>
              )}
              <img src={imgUrl(img)} alt="" style={S.imgTag} onError={(e) => { e.target.style.display = 'none'; }} />
              <div
                title={imgUrl(img)}
                style={{
                  position: 'absolute', top: 4, right: 4, maxWidth: 'calc(100% - 8px)',
                  background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 9, lineHeight: 1.3,
                  padding: '1px 5px', borderRadius: 3, fontFamily: 'ui-monospace, monospace',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {assetId(img)}
              </div>
            <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6, display: 'flex', gap: 6 }}>
                {!sameImg(img, form.images.primary) && (
                  <button style={CHIP_MAIN} onClick={() => makeMain(img)}>Main</button>
                )}
                <button style={CHIP_REMOVE} onClick={() => removeImage(img)}>Remove</button>
            </div>
          </div>
          ))}
          {pending.map((f, i) => (
            <div key={'p' + i} style={{ ...S.imgItem(false), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: C.faint }}>queued</div>
          ))}
          <div style={S.uploadZone} onClick={() => fileRef.current.click()}>
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onPickFiles} />
            + Add
          </div>
        </div>

        {upload && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 4px' }}>
            <UploadMark
              pct={upload.pct}
              label={upload.total > 1 && upload.pct < 100
                ? `${upload.index} of ${upload.total} · ${Math.round(upload.pct / 10) * 10}%`
                : undefined}
            />
          </div>
        )}
        <p style={{ fontSize: 9, color: C.faint }}>
          {isCreate ? 'Images upload when you press Save.' : 'Images upload immediately.'}
        </p>
      </div>

      {/* RIGHT: fields */}
      <div style={S.fields}>
        {deny && <div style={{ ...S.badge('hidden'), padding: 10, color: C.accent }}>{DENY[deny] || `Could not save: ${deny}`}</div>}

        <Field label="Product Name">
          <input style={S.input} maxLength={80} value={form.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Category">
          <select style={S.input} value={form.category}
            onChange={(e) => {
              if (e.target.value === '__new__') { setNewCat(''); return; }
              set('category', e.target.value);
            }}>
            <option value="">— Select —</option>
            {(categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            {/* a category typed here does not exist in the catalogue until the product
                is saved - keep it selectable so a fresh name survives a re-render */}
            {form.category && !(categories || []).includes(form.category) && (
              <option value={form.category}>{form.category}</option>
            )}
            <option value="__new__">+ New category...</option>
          </select>

          {newCat !== null && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input autoFocus style={{ ...S.input, flex: 1 }} maxLength={60}
                placeholder="New category name"
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); if (newCat.trim()) { set('category', newCat.trim()); setNewCat(null); } }
                  if (e.key === 'Escape') setNewCat(null);
                }} />
              <button type="button" style={S.btn('save')} disabled={!newCat.trim()}
                onClick={() => { set('category', newCat.trim()); setNewCat(null); }}>Save</button>
              <button type="button" style={S.btn('ghost')} onClick={() => setNewCat(null)}>Cancel</button>
            </div>
          )}
        </Field>
        <Field label="Short Description">
          <RichText value={form.description} onChange={(v) => set('description', v)} />
        </Field>
        <Field label="Technical Specifications">
          <textarea style={S.textarea} maxLength={1500} value={form.technical_specs} onChange={(e) => set('technical_specs', e.target.value)} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Price (EUR)">
            <input style={S.input} type="number" step="0.01" min="0" value={form.price_euros} onChange={(e) => set('price_euros', e.target.value)} />
          </Field>
          <Field label="Price Type" hint={
`Fixed - the normal case. The customer pays the price you set.

Variable - the customer chooses the amount, for gift vouchers. Quantity is locked to 1.

Enquire - price on request. The product shows in the shop but cannot be added to a cart or checked out.`
          }>
            <select style={S.input} value={form.price_type} onChange={(e) => set('price_type', e.target.value)}>
              {PRICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Stock Status">
          <select style={S.input} value={form.stock_status} onChange={(e) => set('stock_status', e.target.value)}>
            {STOCK_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>

        <div style={S.togglesRow}>
          <Toggle label="Featured" on={form.featured} onClick={() => set('featured', !form.featured)} />
          <Toggle label="Published" on={form.published} onClick={() => set('published', !form.published)} />
          <Toggle label="Hygiene" on={form.hygiene_sensitive} onClick={() => set('hygiene_sensitive', !form.hygiene_sensitive)} />
          <Toggle label="Large Box" on={form.large_box} onClick={() => set('large_box', !form.large_box)} />
        </div>

        {/* CHOICES (axes) - the generator for variants */}
        <Field label="Choices (colour, size, ...)" hint={AXIS_HELP}>
          {axes.map((a, i) => (
            <div key={i} style={S.axisRow}>
              <input style={S.vInput} maxLength={40} placeholder="Choice name (e.g. Colour)"
                value={a.name || ''} onChange={(e) => setAxis(i, 'name', e.target.value)} />
              <input style={S.vInput} maxLength={500} placeholder="Blue, Red, Green, ..."
                value={a.values_raw != null ? a.values_raw : (a.values || []).join(', ')}
                onChange={(e) => setAxis(i, 'values_raw', e.target.value)} />
              <button type="button" style={{ ...S.btn('danger'), padding: '4px 8px', fontSize: 9 }}
                onClick={() => removeAxis(i)}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
            <button type="button" style={S.miniBtn} onClick={addAxis}>+ Add choice</button>
            {axes.length > 0 && (
              <button type="button" style={{ ...S.miniBtn, color: C.text, borderColor: 'rgba(255,255,255,0.25)' }}
                onClick={generateVariants}
                title="Create a variant for every combination. Existing rows are never changed.">
                Generate variants
              </button>
            )}
          </div>
        </Field>

        {/* VARIANTS */}
        <Field label={`Variants (${form.variants.length})`} hint={VARIANT_HELP}>
          <input ref={vFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickVariantFile} />
          {form.variants.map((v, i) => (
            <div key={v.variant_id || i} style={S.varRow}>
              {imgUrl(v.image)
                ? <img src={imgUrl(v.image)} alt="" style={S.varThumb}
                    title={assetId(v.image)}
                    onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                : <div style={S.varThumbEmpty}>no<br />photo</div>}

              <input style={S.vInput} placeholder="Name (e.g. Blue / Concert)" value={v.name || ''}
                onChange={(e) => setVariant(i, 'name', e.target.value)} />

              <input style={S.vInput} type="number" step="0.01" placeholder="€ (blank = product price)"
                value={v.price_cents != null ? v.price_cents / 100 : ''}
                onChange={(e) => setVariant(i, 'price_cents', e.target.value === '' ? null : Math.round(parseFloat(e.target.value) * 100))} />

              <select style={S.vInput} value={v.stock_status || 'in_stock'}
                onChange={(e) => setVariant(i, 'stock_status', e.target.value)}>
                {STOCK_STATUSES.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
              </select>

              <button type="button"
                title={v.published === false ? 'Hidden from the shop - click to show it' : 'Showing in the shop - click to hide it'}
                onClick={() => setVariant(i, 'published', v.published === false)}
                style={{
                  ...S.miniBtn,
                  color: v.published === false ? C.faint : C.success,
                  border: `1px solid ${v.published === false ? 'rgba(255,255,255,0.15)' : 'rgba(45,158,45,0.4)'}`,
                }}>
                {v.published === false ? 'Hidden' : 'Shown'}
              </button>

              <button type="button" style={S.miniBtn} onClick={() => pickVariantImage(i)}
                title={form.slug ? 'Upload a photo for this one' : 'Save the product once first'}>
                {vUpload && vUpload.index === i
                  ? (vUpload.pct >= 100 ? 'Processing' : `${Math.round(vUpload.pct / 10) * 10}%`)
                  : imgUrl(v.image) ? 'Replace' : 'Photo'}
              </button>

              <button type="button" style={{ ...S.btn('danger'), padding: '4px 8px', fontSize: 9 }}
                onClick={() => removeVariant(i)}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
            <button type="button" style={S.miniBtn} onClick={addVariant}>+ Add one by hand</button>
          </div>
          {isCreate && (
            <p style={{ fontSize: 9, color: C.faint, marginTop: 6 }}>
              Save the product once before adding variant photos.
            </p>
          )}
        </Field>

        {/* OPTIONS */}
        <Field label="Options" hint={
`An option is a choice that does not change what you are selling: a gift message, engraving text, a free extra.

It has no price of its own and no stock of its own. If the choice DOES have its own stock or price - a colour, a size - it belongs in Variants above, not here.`
        }>
          {form.options.map((o, i) => (
            <div key={i} style={S.vRow}>
              <input style={S.vInput} placeholder="Option name (e.g. Engraving)" value={o.name || ''} onChange={(e) => setOption(i, 'name', e.target.value)} />
              <select style={S.vInput} value={o.type || 'select'} onChange={(e) => setOption(i, 'type', e.target.value)}>
                <option value="select">select</option><option value="text">text</option><option value="number">number</option>
              </select>
                            <input style={S.vInput} placeholder="choices, comma-sep" value={o.choices_raw != null ? o.choices_raw : (o.choices || []).join(', ')} onChange={(e) => setOption(i, 'choices_raw', e.target.value)} />
              <button style={{ ...S.btn('danger'), padding: '4px 8px', fontSize: 9 }} onClick={() => removeOption(i)}>×</button>
            </div>
          ))}
          <button style={{ ...S.btn('ghost'), fontSize: 9, padding: '6px 12px' }} onClick={addOption}>+ Add option</button>
        </Field>

        <div style={S.actions}>
          <button style={S.btn('save')} disabled={saving} onClick={() => save()}>{saving ? 'Saving…' : 'Save & Publish'}</button>
          <button style={S.btn('ghost')} disabled={saving} onClick={() => save(false)}>Save as Draft</button>
          {form.status === 'archived' ? (
            <button style={S.btn('ghost')} disabled={saving}
              title="Bring this product back into your shop"
              onClick={() => save(null, 'active')}>Restore to shop</button>
          ) : (
            <button style={S.btn('ghost')} disabled={saving}
              title="Remove from the shop without deleting anything"
              onClick={() => {
                if (window.confirm(
                  `Archive "${form.name || 'this product'}"?\n\n`
                  + 'It is removed from your shop straight away and can no longer be bought.\n\n'
                  + 'Nothing is deleted. Past orders keep their history, and you can bring it '
                  + 'back any time with "Show archived".'
                )) save(null, 'archived');
              }}>Archive</button>
          )}
          <button style={S.btn('ghost')} disabled={saving} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── product row (collapsed + expandable edit) ───────────────────────────────
function ProductRow({ p, expanded, onToggle, onSaved, setStatus, activeGido, categories }) {
  const [busy, setBusy] = useState(false);
  const isArchived = p.status === 'archived';

  // Archive is NOT delete, and this is the reason there is no delete button anywhere in
  // this page. A product can be referenced by a live order line, and both shops have
  // live orders - removing the record would break that order's history, so a shop owner
  // could destroy her own records by tidying her catalogue. Setting status instead is
  // already honoured everywhere that matters: the storefront query filters
  // status == "active" AND published == true, and is_purchasable refuses anything whose
  // status is not active. The product leaves the shop immediately; nothing past is touched.
  //
  // The body is the FULL product. save-product is read-modify-write, so a body missing
  // description, technical_specs, variants or options writes them back as blanks - the
  // Korala wipe, 2026-09-09. Nothing here is a partial update.
  async function toggleArchive(e) {
    e.stopPropagation();                      // the row head is the expand control
    const next = isArchived ? 'active' : 'archived';
    if (!isArchived && !window.confirm(
      `Archive "${p.name || 'this product'}"?\n\n`
      + 'It is removed from your shop straight away and can no longer be bought.\n\n'
      + 'Nothing is deleted. Past orders keep their history, and you can bring it back '
      + 'any time with "Show archived".'
    )) return;

    setBusy(true);
    try {
      const { res, data } = await saveProduct({
        gido_id: activeGido,
        product_id: p.product_id, slug: p.slug,
        name: p.name, category: p.category, description: p.description,
        technical_specs: p.technical_specs,
        price_euros: Number(p.price || 0),
        stock_status: p.stock_status, price_type: p.price_type,
        status: next,
        featured: !!p.featured, published: !!p.published,
        large_box: !!p.large_box, hygiene_sensitive: !!p.hygiene_sensitive,
        images: { primary: p.primary_image || '', secondary: p.secondary_image || '', all: p.all_assets || [] },
        variants: p.variants || [],
        options: p.options || [],
      });
      if (!res.ok || !data.ok) {
        setStatus(`Could not ${next === 'archived' ? 'archive' : 'restore'}: ${data.reason || res.status}`, 'error');
      } else {
        setStatus(next === 'archived' ? 'Archived' : 'Restored', 'success');
        onSaved(p.product_id);
      }
    } catch (err) {
      setStatus(`Network error: ${err.message}`, 'error');
    }
    setBusy(false);
  }

  return (
    <div style={{ ...S.row, opacity: isArchived ? 0.55 : 1 }}>
      <div style={S.rowHead} onClick={onToggle}>
        <img style={S.thumb} src={p.primary_image || ''} alt="" onError={(e) => { e.target.style.background = C.bg4; }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.rowName}>{p.name || 'Unnamed'}</div>
          <div style={S.rowCat}>
            {p.category || ''} · {(p.all_assets || []).length} image(s)
            {(p.variants || []).length ? ` · ${p.variants.length} variant(s)` : ''}
          </div>
        </div>
        <div style={S.rowPrice}>€{Number(p.price || 0).toFixed(2)}</div>
        {isArchived
          ? <div style={S.badge('archived')}>Archived</div>
          : <div style={S.badge(p.published ? 'live' : 'hidden')}>{p.published ? 'Live' : 'Hidden'}</div>}
        <button type="button" onClick={toggleArchive} disabled={busy}
          title={isArchived
            ? 'Bring this product back into your shop'
            : 'Remove from the shop without deleting anything'}
          style={{
            background: 'none',
            border: `1px solid ${isArchived ? 'rgba(45,158,45,0.4)' : 'rgba(255,255,255,0.18)'}`,
            color: isArchived ? C.success : C.faint,
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
            padding: '4px 9px', cursor: busy ? 'default' : 'pointer', flexShrink: 0,
            opacity: busy ? 0.5 : 1,
          }}>
          {busy ? '...' : isArchived ? 'Restore' : 'Archive'}
        </button>
        <div style={{ color: C.faint, fontSize: 12 }}>{expanded ? '▴' : '▾'}</div>
      </div>
      {expanded && (
        <ProductForm initial={productToForm(p)} onSaved={onSaved} onCancel={onToggle} setStatus={setStatus} activeGido={activeGido} categories={categories} />
      )}
    </div>
  );
}

// Statuses that belong to the SHOP. Everything else in `catalogue` belongs to
// the SmartData review pipeline - flagged_duplicate, kept_for_later, linked,
// rejected - which parks its records under the category 'unreviewed' and never
// publishes them. 312 of GIDO_IE_0001's 589 records are the pipeline's.
//
// An ALLOW-list, not a deny-list, deliberately. The day the pipeline adds a
// fifth status, a deny-list would silently let it into an owner's category list
// and offer to rename 300 records he has never seen and does not own.
const SHOP_STATUSES = new Set(['active', 'archived']);

// The stored shape of a taxonomy, built in exactly one place. Three surfaces
// write this document - the structure panel, the rename list and the menu links
// - and the endpoint stores a tree as a whole, so each of them has to send every
// field back. Three copies of this mapper is three chances for one of them to
// drop a path or an icon on save (clause 11).
function taxonomyPayload(list) {
  return (Array.isArray(list) ? list : []).map((n) => ({
    id: n.id,
    value: n.value || null,
    parent: n.parent || null,
    label: n.label || '',
    icon: n.icon || null,
    path: n.path || null,
    order: Number.isInteger(n.order) ? n.order : 0,
    policy: n.policy || 'auto',
    axis: n.axis || 'category',
  }));
}

// Renaming a catalogue value is two writes that must BOTH happen: every product
// carrying the old string, and every menu entry selecting it. The only thing in
// Categories that writes a product.
//
// Products go first and the taxonomy finishes. Either order leaves a brief
// window where the entry looks empty, so the order is chosen for RECOVERY rather
// than appearance: if the taxonomy write fails, the products carry the new name
// and the panel shows it under "not in the structure", which is visible and
// fixable. The reverse leaves an entry pointing at a name no product has, which
// looks identical to an empty category and hides the failure.
//
// A partial product rename stops before the taxonomy write. Half the products
// under a new name is bad; half the products under a new name plus a menu entry
// that has already moved to it is worse, because the ones left behind become
// unreachable.
//
// The web address is NOT touched. Five of GIDO_IE_0001's paths already differ
// from their labels, and deriving a url from a new name would rewrite links
// indexed since spring.
async function renameCategoryEverywhere({ oldValue, target, products, taxonomy, activeGido, onProgress }) {
  const affected = products.filter((p) => (p.category || '') === oldValue);
  const touching = (taxonomy || []).filter((n) => (n.value || '') === oldValue);
  let done = 0;
  let failed = 0;
  for (const p of affected) {
    const body = {
      gido_id: activeGido,
      product_id: p.product_id, slug: p.slug,
      name: p.name, category: target, description: p.description,
      technical_specs: p.technical_specs,
      price_euros: Number(p.price || 0),
      stock_status: p.stock_status, price_type: p.price_type,
      status: p.status || 'active',   // a rename must never republish an archived product
      featured: !!p.featured, published: !!p.published,
      large_box: !!p.large_box, hygiene_sensitive: !!p.hygiene_sensitive,
      images: { primary: p.primary_image || '', secondary: p.secondary_image || '', all: p.all_assets || [] },
      variants: p.variants || [],
      options: p.options || [],
    };
    try {
      const { res, data } = await saveProduct(body);
      if (res.ok && data.ok) done += 1; else failed += 1;
    } catch { failed += 1; }
    onProgress(`${done + failed} of ${affected.length}`);
  }
  if (failed) {
    return { ok: false, done, failed, total: affected.length, stage: 'products' };
  }
  const next = (taxonomy || []).map((n) => ((n.value || '') === oldValue ? { ...n, value: target } : n));
  try {
    const { res, data } = await saveTaxonomy({ gido_id: activeGido, taxonomy: taxonomyPayload(next) });
    if (!res.ok || !data.ok) {
      return { ok: false, done, failed: 0, total: affected.length, stage: 'taxonomy',
               problems: data.problems && data.problems.length ? data.problems : [data.reason || `HTTP ${res.status}`] };
    }
  } catch (e) {
    return { ok: false, done, failed: 0, total: affected.length, stage: 'taxonomy',
             problems: [String(e && e.message ? e.message : e)] };
  }
  return { ok: true, done, entries: touching.length };
}

// ── Structure panel ─────────────────────────────────────────────────────────
// The tree as the owner declared it, with live counts, plus the thing they could
// never see before: which of their categories are NOT in it.
//
// WHAT THIS PANEL CAN DO, and the line it does not cross. It renames a menu
// entry, moves one within its siblings or under a different parent, points one
// at a different category, adds one, removes one, and sets whether it shows.
// Every one of those is a config write. NOTHING here writes a product - renaming
// the category itself lives in the Rename section, because it costs hundreds of
// writes and the owner should have to go somewhere else to spend them.
//
// A node is CREATED here rather than in the product form (D-136). A category
// typed into a product field is a string with no parent, no order, no icon and
// no visibility policy, and inventing a node from it means inventing four
// answers the owner never gave.
//
// Visibility is NOT re-derived here. The storefront's resolver decides it
// (clause 1); this panel PREVIEWS the same decision from the same inputs so an
// owner can see the consequence before saving - including that hiding a branch
// hides everything under it.
function StructurePanel({ taxonomy, taxonomyHealth, products, activeGido, setStatus, onSaved }) {
  const stored = Array.isArray(taxonomy) ? taxonomy : [];

  const [draft, setDraft] = useState(() => stored.map((n) => ({ ...n })));
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newParent, setNewParent] = useState('');

  useEffect(() => {
    setDraft(stored.map((n) => ({ ...n })));
    setProblems([]);
    setOpenId(null);
    setConfirmId(null);
  }, [taxonomy]);

  const nodes = draft;

  const live = {};
  const total = {};
  for (const p of products) {
    const c = p.category || '';
    if (!c) continue;
    total[c] = (total[c] || 0) + 1;
    if ((p.status || 'active') === 'active' && p.published) live[c] = (live[c] || 0) + 1;
  }

  const ids = new Set(nodes.map((n) => n.id).filter(Boolean));
  const byParent = {};
  for (const n of nodes) {
    if (!n.id) continue;
    const key = n.parent && ids.has(n.parent) && n.parent !== n.id ? n.parent : '__root__';
    (byParent[key] = byParent[key] || []).push(n);
  }
  for (const k of Object.keys(byParent)) {
    byParent[k].sort(
      (a, b) =>
        (Number.isInteger(a.order) ? a.order : 0) - (Number.isInteger(b.order) ? b.order : 0) ||
        String(a.label || '').localeCompare(String(b.label || ''))
    );
  }

  const places = {};
  for (const n of nodes) {
    if (n.value) (places[n.value] = places[n.value] || []).push(n.id);
  }

  const claimed = new Set(nodes.map((n) => n.value).filter(Boolean));
  const outside = Object.keys(total)
    .filter((c) => !claimed.has(c))
    .sort((a, b) => (total[b] || 0) - (total[a] || 0) || a.localeCompare(b));

  function subtree(n, depth) {
    if (depth > 8) return 0;
    const own = n.value ? live[n.value] || 0 : 0;
    return own + (byParent[n.id] || []).reduce((s, k) => s + subtree(k, depth + 1), 0);
  }

  function descendantsOf(id, depth) {
    const out = new Set();
    if (depth > 8) return out;
    for (const k of byParent[id] || []) {
      out.add(k.id);
      for (const d of descendantsOf(k.id, depth + 1)) out.add(d);
    }
    return out;
  }

  function slug(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function freshId(label) {
    const base = slug(label) || 'entry';
    if (!ids.has(base)) return base;
    for (let i = 2; i < 500; i += 1) {
      if (!ids.has(`${base}-${i}`)) return `${base}-${i}`;
    }
    return `${base}-${Date.now()}`;
  }

  function touched() {
    setProblems([]);
    setWarnings([]);
  }

  function patch(id, change) {
    setDraft((d) => d.map((n) => (n.id === id ? { ...n, ...change } : n)));
    touched();
  }

  // Order is rewritten for the whole sibling group, not just the two that moved.
  // A group seeded with duplicate or missing order values cannot be sorted
  // stably by swapping two of them.
  function move(node, delta) {
    const parentKey = node.parent && ids.has(node.parent) ? node.parent : '__root__';
    const group = (byParent[parentKey] || []).slice();
    const i = group.findIndex((n) => n.id === node.id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= group.length) return;
    const reordered = group.slice();
    reordered.splice(j, 0, reordered.splice(i, 1)[0]);
    const newOrder = {};
    reordered.forEach((n, k) => { newOrder[n.id] = k; });
    setDraft((d) => d.map((n) => (n.id in newOrder ? { ...n, order: newOrder[n.id] } : n)));
    touched();
  }

  // A moved entry lands at the END of its new siblings rather than at whatever
  // position its old order happened to be - arriving in the middle of a group
  // the owner already arranged is the surprising outcome.
  function reparent(id, parentId) {
    const target = parentId || null;
    const size = (byParent[target || '__root__'] || []).length;
    setDraft((d) => d.map((n) => (n.id === id ? { ...n, parent: target, order: size } : n)));
    touched();
  }

  // Removing an entry removes a MENU ENTRY and nothing else. Its products keep
  // their category and stay on the shop; they move into "not in the structure"
  // until some entry selects them again.
  function removeNode(id) {
    setDraft((d) => d.filter((n) => n.id !== id));
    setOpenId(null);
    setConfirmId(null);
    touched();
  }

  function addNode() {
    const label = newLabel.trim();
    if (!label) return;
    const parent = newParent && ids.has(newParent) ? newParent : null;
    const node = {
      id: freshId(label),
      value: newValue || null,
      parent,
      label,
      icon: null,
      path: null,            // never derived here - the storefront owns the url
      order: (byParent[parent || '__root__'] || []).length,
      policy: 'auto',
      axis: 'category',
    };
    setDraft((d) => d.concat([node]));
    setNewLabel('');
    setNewValue('');
    setNewParent('');
    setAdding(false);
    touched();
  }

  const storedById = {};
  for (const n of stored) storedById[n.id] = n;
  const draftIds = new Set(nodes.map((n) => n.id));
  const added = nodes.filter((n) => !storedById[n.id]);
  const removed = stored.filter((n) => !draftIds.has(n.id));
  const edited = nodes.filter((n) => {
    const o = storedById[n.id];
    if (!o) return false;
    return (
      (n.label || '') !== (o.label || '') ||
      (Number.isInteger(n.order) ? n.order : 0) !== (Number.isInteger(o.order) ? o.order : 0) ||
      (n.policy || 'auto') !== (o.policy || 'auto') ||
      (n.parent || null) !== (o.parent || null) ||
      (n.value || null) !== (o.value || null)
    );
  });
  const changeCount = added.length + removed.length + edited.length;
  const dirty = changeCount > 0;

  async function save() {
    setBusy(true);
    setProblems([]);
    setWarnings([]);
    try {
      const { res, data } = await saveTaxonomy({ gido_id: activeGido, taxonomy: taxonomyPayload(nodes) });
      if (!res.ok || !data.ok) {
        setProblems(data.problems && data.problems.length ? data.problems : [data.reason || `HTTP ${res.status}`]);
        setStatus('Structure not saved - see the panel.', 'error');
        setBusy(false);
        return;
      }
      setWarnings(data.warnings || []);
      setStatus(`Structure saved - ${data.nodes} entries.`, 'success');
      setBusy(false);
      onSaved();
    } catch (e) {
      setProblems([String(e && e.message ? e.message : e)]);
      setStatus('Structure not saved - network error.', 'error');
      setBusy(false);
    }
  }

  const chip = {
    fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    padding: '2px 6px', border: `1px solid ${C.line}`, color: C.faint2, whiteSpace: 'nowrap',
  };
  const nudge = { ...S.btn('ghost'), padding: '2px 7px', fontSize: 10, lineHeight: 1.1 };
  const labelInput = {
    ...S.input, flex: 1, minWidth: 90, padding: '3px 6px', fontSize: 13, color: C.text,
    background: 'transparent', border: '1px solid transparent', borderBottom: `1px dashed ${C.line}`,
  };
  const labelInputEdited = { ...labelInput, border: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` };
  const miniSelect = { ...S.input, padding: '3px 4px', fontSize: 10, color: C.text };
  const fieldLabel = { fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.faint2 };

  function parentChoices(id) {
    const banned = id ? descendantsOf(id, 0) : new Set();
    return nodes
      .filter((n) => n.id !== id && !banned.has(n.id))
      .slice()
      .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
  }

  function renderEditor(n, depth) {
    const kids = byParent[n.id] || [];
    const blocked = kids.length > 0;
    return (
      <div
        key={`${n.id}-editor`}
        style={{
          padding: '10px 12px', paddingLeft: 8 + depth * 18 + 28,
          borderBottom: `1px solid ${C.line2}`, background: C.bg,
          display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={fieldLabel}>Sits under</span>
          <select
            style={{ ...miniSelect, fontSize: 11, minWidth: 180 }}
            value={n.parent || ''}
            disabled={busy}
            onChange={(e) => reparent(n.id, e.target.value)}
          >
            <option value="">Top level</option>
            {parentChoices(n.id).map((p) => (
              <option key={p.id} value={p.id}>{p.label || p.id}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 220 }}>
          <span style={fieldLabel}>Shows products from</span>
          <select style={{ ...miniSelect, fontSize: 11 }} value={n.value || ''} disabled={busy}
            onChange={(e) => patch(n.id, { value: e.target.value || null })}>
            <option value="">nothing - a heading only</option>
            {allCategories.map((c) => (
              <option key={c} value={c}>{c} ({live[c] || 0} live)</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={fieldLabel}>Web address</span>
          <span style={{ fontSize: 11, color: C.faint2 }}>{n.path || 'chosen by the shop'}</span>
        </div>

        {confirmId === n.id ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 260 }}>
            <span style={{ fontSize: 11, color: C.text }}>
              Remove "{n.label}" from the menu?
              {n.value ? ` Its ${total[n.value] || 0} product${(total[n.value] || 0) === 1 ? '' : 's'} stay on the shop.` : ''}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" style={S.btn('save')} disabled={busy} onClick={() => removeNode(n.id)}>Remove</button>
              <button type="button" style={S.btn('ghost')} disabled={busy} onClick={() => setConfirmId(null)}>Keep</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={fieldLabel}>Menu entry</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                style={S.btn('ghost')}
                disabled={busy || blocked}
                onClick={() => setConfirmId(n.id)}
              >
                Remove
              </button>
              {blocked && (
                <span
                  style={{ ...chip, cursor: 'help', borderRadius: '50%', padding: '2px 7px' }}
                  title={`"${n.label}" has ${kids.length} entr${kids.length === 1 ? 'y' : 'ies'} under it. Move or remove those first. Products never block removing an entry - they stay on the shop either way.`}
                >
                  i
                </span>
              )}
            </span>
          </div>
        )}
      </div>
    );
  }

  // NOT components. A component function declared inside this one gets a new
  // identity on every render, so React treats it as a different type and
  // unmounts the whole subtree on each keystroke.
  function renderRow(n, depth, parentHidden) {
    const own = n.value ? live[n.value] || 0 : 0;
    const ownAll = n.value ? total[n.value] || 0 : 0;
    const kids = byParent[n.id] || [];
    const sub = subtree(n, 0);
    const policy = n.policy || 'auto';
    const selfHidden = policy === 'hidden';
    const hidden = selfHidden || parentHidden;
    const empty = sub === 0;
    const shownToVisitors = !hidden && (sub > 0 || policy === 'always');
    const dup = n.value && (places[n.value] || []).length > 1;
    const siblingKey = n.parent && ids.has(n.parent) ? n.parent : '__root__';
    const siblingGroup = byParent[siblingKey] || [];
    const i = siblingGroup.findIndex((x) => x.id === n.id);
    const siblings = siblingGroup.length;
    const isEdited = edited.some((c) => c.id === n.id);
    const isNew = !storedById[n.id];
    const open = openId === n.id;

    return (
      <React.Fragment key={n.id}>
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 8px', paddingLeft: 8 + depth * 18,
            borderBottom: `1px solid ${C.line2}`,
            background: isEdited || isNew ? C.bg : 'transparent',
            opacity: shownToVisitors ? 1 : 0.5,
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <button type="button" style={nudge} disabled={i <= 0 || busy} onClick={() => move(n, -1)} title="Move up">
              {'\u25B2'}
            </button>
            <button type="button" style={nudge} disabled={i < 0 || i >= siblings - 1 || busy} onClick={() => move(n, 1)} title="Move down">
              {'\u25BC'}
            </button>
          </span>

          {n.icon ? <span style={{ fontSize: 13, width: 18, textAlign: 'center' }}>{n.icon}</span> : <span style={{ width: 18 }} />}

          <input
            style={isEdited || isNew ? labelInputEdited : labelInput}
            value={n.label || ''}
            maxLength={60}
            disabled={busy}
            onChange={(e) => patch(n.id, { label: e.target.value })}
          />

          {isNew && <span style={chip}>new</span>}
          {!n.value && <span style={chip}>group</span>}
          {dup && <span style={chip}>in {(places[n.value] || []).length} places</span>}

          {n.value ? (
            <span style={{ fontSize: 11, color: own ? C.faint : C.faint2, whiteSpace: 'nowrap' }}>
              {own} live{ownAll !== own ? ` of ${ownAll}` : ''}
            </span>
          ) : null}

          {kids.length > 0 && (
            <span style={{ fontSize: 11, color: C.faint2, whiteSpace: 'nowrap' }}>{sub} in branch</span>
          )}

          <select
            style={{ ...miniSelect, width: 128 }}
            value={policy}
            disabled={busy}
            onChange={(e) => patch(n.id, { policy: e.target.value })}
          >
            <option value="auto">When it has products</option>
            <option value="always">Always show</option>
            <option value="hidden">Never show</option>
          </select>

          <span style={{ fontSize: 10, color: C.faint2, width: 90, textAlign: 'right', whiteSpace: 'nowrap' }}>
            {selfHidden ? 'hidden'
              : parentHidden ? 'parent hidden'
              : empty && policy === 'always' ? 'empty, kept on'
              : empty ? 'empty, not shown'
              : ''}
          </span>

          <button
            type="button"
            style={{ ...nudge, fontSize: 12, width: 28 }}
            disabled={busy}
            title="Move it, point it elsewhere, or remove it"
            onClick={() => { setOpenId(open ? null : n.id); setConfirmId(null); }}
          >
            {open ? '\u2212' : '\u22EF'}
          </button>
        </div>

        {open && renderEditor(n, depth)}

        {kids.map((k) => renderRow(k, depth + 1, hidden))}
      </React.Fragment>
    );
  }

  const roots = byParent.__root__ || [];
  const inTree = roots.reduce((s, n) => s + subtree(n, 0), 0);
  const outsideLive = outside.reduce((s, c) => s + (live[c] || 0), 0);
  const allCategories = Object.keys(total).sort((a, b) => a.localeCompare(b));

  const faults = [];
  if (taxonomyHealth && taxonomyHealth.ok === false) {
    for (const l of taxonomyHealth.missing_id || []) faults.push(`"${l}" has no id`);
    for (const i of taxonomyHealth.duplicate_id || []) faults.push(`two entries share the id "${i}"`);
    for (const o of taxonomyHealth.orphan || []) faults.push(`"${o.id}" sits under "${o.missing_parent}", which does not exist`);
    for (const i of taxonomyHealth.self_parent || []) faults.push(`"${i}" is its own parent`);
  }

  return (
    <div>
      {faults.length > 0 && (
        <div style={{ padding: '10px 12px', border: `1px solid ${C.line}`, margin: '0 0 12px' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.text, margin: '0 0 6px' }}>Problems in the structure</p>
          {faults.map((f, i) => (<p key={i} style={{ fontSize: 11, color: C.faint, margin: '0 0 3px' }}>{f}</p>))}
          <p style={{ fontSize: 10, color: C.faint2, margin: '6px 0 0' }}>The shop still renders - these entries are skipped.</p>
        </div>
      )}

      {problems.length > 0 && (
        <div style={{ padding: '10px 12px', border: `1px solid ${C.line}`, margin: '0 0 12px' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.text, margin: '0 0 6px' }}>Not saved</p>
          {problems.map((f, i) => (<p key={i} style={{ fontSize: 11, color: C.faint, margin: '0 0 3px' }}>{f}</p>))}
          <p style={{ fontSize: 10, color: C.faint2, margin: '6px 0 0' }}>Nothing was written. The shop is unchanged.</p>
        </div>
      )}

      {warnings.length > 0 && (
        <div style={{ padding: '10px 12px', border: `1px solid ${C.line2}`, margin: '0 0 12px' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.text, margin: '0 0 6px' }}>Saved, with notes</p>
          {warnings.map((f, i) => (<p key={i} style={{ fontSize: 11, color: C.faint, margin: '0 0 3px' }}>{f}</p>))}
        </div>
      )}

      {nodes.length === 0 ? (
        <p style={{ fontSize: 12, color: C.faint, padding: '10px 12px', margin: 0 }}>
          No structure set up yet. Every category sits at the top level of the shop.
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 8px', padding: '0 2px' }}>
            <p style={{ fontSize: 11, color: C.faint2, margin: 0, flex: 1 }}>
              {nodes.length} entries, {inTree} live products reachable through them.
              Dimmed rows are not shown to visitors.
            </p>
            {dirty && (
              <span style={{ fontSize: 11, color: C.faint, whiteSpace: 'nowrap' }}>
                {changeCount} change{changeCount === 1 ? '' : 's'}
              </span>
            )}
            <button
              type="button"
              style={S.btn('ghost')}
              disabled={!dirty || busy}
              onClick={() => { setDraft(stored.map((n) => ({ ...n }))); setOpenId(null); setConfirmId(null); touched(); }}
            >
              Revert
            </button>
            <button type="button" style={S.btn('save')} disabled={!dirty || busy} onClick={save}>
              {busy ? 'Saving...' : 'Save structure'}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, padding: '0 8px 3px' }}>
            <span style={{ flex: 1 }} />
            <span style={{ ...fieldLabel, width: 128 }}>When this shows up</span>
            <span style={{ width: 90 }} />
            <span style={{ width: 28 }} />
          </div>

          <div style={{ border: `1px solid ${C.line2}` }}>
            {roots.map((n) => renderRow(n, 0, false))}
          </div>

          <div style={{ marginTop: 8 }}>
            {adding ? (
              <div style={{ border: `1px solid ${C.line}`, padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 160 }}>
                  <span style={fieldLabel}>Menu wording</span>
                  <input autoFocus style={{ ...S.input, fontSize: 13, color: C.text }} maxLength={60}
                    value={newLabel} disabled={busy} onChange={(e) => setNewLabel(e.target.value)} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={fieldLabel}>Shows products from</span>
                  <select style={{ ...miniSelect, fontSize: 11, minWidth: 190 }} value={newValue} disabled={busy}
                    onChange={(e) => setNewValue(e.target.value)}>
                    <option value="">nothing - a heading only</option>
                    {allCategories.map((c) => (
                      <option key={c} value={c}>
                        {c} ({live[c] || 0} live){claimed.has(c) ? ' - already in the menu' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={fieldLabel}>Sits under</span>
                  <select style={{ ...miniSelect, fontSize: 11, minWidth: 170 }} value={newParent} disabled={busy}
                    onChange={(e) => setNewParent(e.target.value)}>
                    <option value="">Top level</option>
                    {parentChoices(null).map((p) => (<option key={p.id} value={p.id}>{p.label || p.id}</option>))}
                  </select>
                </div>
                <button type="button" style={S.btn('save')} disabled={busy || !newLabel.trim()} onClick={addNode}>Add</button>
                <button type="button" style={S.btn('ghost')} disabled={busy} onClick={() => setAdding(false)}>Cancel</button>
              </div>
            ) : (
              <button type="button" style={S.btn('ghost')} disabled={busy} onClick={() => setAdding(true)}>
                Add a menu entry
              </button>
            )}
          </div>

          <p style={{ fontSize: 10, color: C.faint2, margin: '8px 2px 0' }}>
            Nothing in here writes a product. Changing the wording changes the menu,
            moving an entry changes where it sits, removing one takes it out of the
            menu - its products stay on the shop either way. To change a category name
            on the products themselves, use Rename category.
          </p>
        </>
      )}

      {outside.length > 0 && (
        <div style={{ margin: '14px 0 0' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.text, margin: '0 0 2px' }}>
            Not in the structure ({outside.length})
          </p>
          <p style={{ fontSize: 10, color: C.faint2, margin: '0 0 8px' }}>
            {outsideLive} live product{outsideLive === 1 ? '' : 's'} in these.
            They are still on the shop, reachable through search and the catch-all, but not in the menu.
            Use "Add a menu entry" to put one in.
          </p>
          <div style={{ border: `1px solid ${C.line2}` }}>
            {outside.map((c) => (
              <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderBottom: `1px solid ${C.line2}` }}>
                <span style={{ fontSize: 13, color: C.text, flex: 1 }}>{c}</span>
                <span style={{ fontSize: 11, color: C.faint2, whiteSpace: 'nowrap' }}>
                  {live[c] || 0} live of {total[c] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rename a category ───────────────────────────────────────────────────────
// The flat list, every category the shop actually uses, alphabetical. It exists
// alongside the tree rather than inside it because the two answer different
// questions: the tree asks where something sits, this asks what something is
// called. A category that is in no menu entry still appears here - which the
// tree alone could never offer, since it has no row to hang it on.
//
// One Rename at a time, on purpose. It writes products one by one, and a second
// rename started mid-flight would be writing against a catalogue that is moving.
function RenamePanel({ products, taxonomy, activeGido, setStatus, onSaved }) {
  const [target, setTarget] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [problems, setProblems] = useState([]);

  const live = {};
  const total = {};
  for (const p of products) {
    const c = p.category || '';
    if (!c) continue;
    total[c] = (total[c] || 0) + 1;
    if ((p.status || 'active') === 'active' && p.published) live[c] = (live[c] || 0) + 1;
  }
  const cats = Object.keys(total).sort((a, b) => a.localeCompare(b));
  const nodes = Array.isArray(taxonomy) ? taxonomy : [];
  const inMenu = {};
  for (const n of nodes) if (n.value) inMenu[n.value] = (inMenu[n.value] || 0) + 1;

  async function run(oldValue) {
    const next = (name || '').trim();
    if (!next || next === oldValue) { setTarget(null); return; }
    setBusy(true);
    setProblems([]);
    setProgress(`0 of ${total[oldValue] || 0}`);
    const r = await renameCategoryEverywhere({
      oldValue, target: next, products, taxonomy: nodes, activeGido, onProgress: setProgress,
    });
    setProgress(null);
    setBusy(false);
    if (!r.ok && r.stage === 'products') {
      setProblems([`${r.failed} of ${r.total} products could not be renamed, so the menu entry was left alone - nothing points at a name only some products carry. Reload and try again.`]);
      setStatus(`Renamed ${r.done}, ${r.failed} failed - reload and check.`, 'error');
      return;
    }
    if (!r.ok) {
      setProblems((r.problems || []).concat([
        `The ${r.done} products now carry "${next}", but the menu entry could not be updated. It is listed under "not in the structure" until an entry points at it.`,
      ]));
      setStatus('Products renamed, menu entry not updated.', 'error');
      return;
    }
    setTarget(null);
    setName('');
    setStatus(
      `Renamed ${r.done} product${r.done === 1 ? '' : 's'} and ${r.entries} menu entr${r.entries === 1 ? 'y' : 'ies'} to "${next}".`,
      'success'
    );
    onSaved();
  }

  const chip = {
    fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    padding: '2px 6px', border: `1px solid ${C.line}`, color: C.faint2, whiteSpace: 'nowrap',
  };

  return (
    <div>
      <p style={{ fontSize: 11, color: C.faint2, margin: '0 0 10px', padding: '0 2px' }}>
        {cats.length} categories in use. Renaming one rewrites every product in it and
        moves its menu entries with it. Web addresses never change.
      </p>

      {problems.length > 0 && (
        <div style={{ padding: '10px 12px', border: `1px solid ${C.line}`, margin: '0 0 12px' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.text, margin: '0 0 6px' }}>Not finished</p>
          {problems.map((f, i) => (<p key={i} style={{ fontSize: 11, color: C.faint, margin: '0 0 3px' }}>{f}</p>))}
        </div>
      )}

      {cats.length === 0 ? (
        <p style={{ fontSize: 12, color: C.faint, padding: 10, margin: 0 }}>
          No categories yet - they appear as you assign them to products.
        </p>
      ) : (
        <div style={{ border: `1px solid ${C.line2}` }}>
          {cats.map((c) => (
            <div key={c} style={{ borderBottom: `1px solid ${C.line2}`, padding: '9px 10px' }}>
              {target === c ? (
                <>
                  <p style={{ fontSize: 11, color: C.faint, margin: '0 0 8px' }}>
                    Renaming "{c}" - rewrites {total[c]} product{total[c] === 1 ? '' : 's'}
                    {inMenu[c] ? ` and ${inMenu[c]} menu entr${inMenu[c] === 1 ? 'y' : 'ies'}` : ', which is in no menu entry'}.
                  </p>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input autoFocus style={{ ...S.input, flex: 1, color: C.text }} maxLength={60} value={name}
                      disabled={busy}
                      onChange={(e) => setName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape' && !busy) setTarget(null); }} />
                    <button type="button" style={S.btn('save')} disabled={busy || !name.trim()} onClick={() => run(c)}>
                      {progress ? `Renaming ${progress}` : 'Save'}
                    </button>
                    <button type="button" style={S.btn('ghost')} disabled={busy} onClick={() => setTarget(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ flex: 1, fontSize: 13, color: C.text }}>{c}</span>
                  {!inMenu[c] && <span style={chip}>not in the menu</span>}
                  <span style={{ fontSize: 11, color: C.faint }}>
                    {total[c]} product{total[c] === 1 ? '' : 's'}
                  </span>
                  <button type="button" style={{ ...S.btn('ghost'), padding: '5px 12px', fontSize: 9 }}
                    disabled={busy}
                    onClick={() => { setTarget(c); setName(c); setProblems([]); }}>Rename</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Declared menu items ─────────────────────────────────────────────────────
// The half of the navigation that references NO catalogue value. Nothing derives
// them, so nothing can restore them - which is why clause 8 separates them from
// the taxonomy and why save-taxonomy treats them as optional, so a structure
// save can never blank them.
//
// A save from here sends the taxonomy back UNCHANGED alongside the declared
// items, because the endpoint stores a tree as a whole. Unsaved edits in the
// structure section are therefore not included - the sections save
// independently and neither carries the other's draft.
function DeclaredPanel({ declared, taxonomy, activeGido, setStatus, onSaved }) {
  const storedItems = Array.isArray(declared) ? declared : [];

  const [draft, setDraft] = useState(() => storedItems.map((d) => ({ ...d })));
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState([]);

  useEffect(() => {
    setDraft(storedItems.map((d) => ({ ...d })));
    setProblems([]);
  }, [declared]);

  const items = draft
    .slice()
    .sort((a, b) => (Number.isInteger(a.order) ? a.order : 0) - (Number.isInteger(b.order) ? b.order : 0));

  function touched() {
    setProblems([]);
  }

  function patchAt(index, change) {
    setDraft(() => items.map((d, i) => (i === index ? { ...d, ...change } : { ...d })));
    touched();
  }

  function move(index, delta) {
    const j = index + delta;
    if (j < 0 || j >= items.length) return;
    const next = items.map((d) => ({ ...d }));
    next.splice(j, 0, next.splice(index, 1)[0]);
    setDraft(next.map((d, i) => ({ ...d, order: i })));
    touched();
  }

  function removeAt(index) {
    setDraft(items.filter((d, i) => i !== index).map((d, i) => ({ ...d, order: i })));
    touched();
  }

  function add() {
    setDraft(items.concat([{ label: '', path: '', order: items.length }]).map((d, i) => ({ ...d, order: i })));
    touched();
  }

  const same =
    items.length === storedItems.length &&
    items.every((d, i) => {
      const o = storedItems[i] || {};
      return (d.label || '') === (o.label || '') && (d.path || '') === (o.path || '');
    });
  const dirty = !same;

  // An item with no wording or no address is not an item. The server drops those
  // silently; saying so here means the owner finds out before the save rather
  // than by noticing something missing from her menu afterwards.
  const incomplete = items.filter((d) => !(d.label || '').trim() || !(d.path || '').trim()).length;
  const badPath = items.filter((d) => (d.path || '').trim() && !(d.path || '').trim().startsWith('/')).length;

  async function save() {
    setBusy(true);
    setProblems([]);
    const cleaned = items
      .filter((d) => (d.label || '').trim() && (d.path || '').trim())
      .map((d, i) => ({ label: d.label.trim(), path: d.path.trim(), order: i }));
    try {
      const { res, data } = await saveTaxonomy({
        gido_id: activeGido,
        taxonomy: taxonomyPayload(taxonomy),
        navigation: { declared: cleaned },
      });
      if (!res.ok || !data.ok) {
        setProblems(data.problems && data.problems.length ? data.problems : [data.reason || `HTTP ${res.status}`]);
        setStatus('Menu links not saved - see the panel.', 'error');
        setBusy(false);
        return;
      }
      setStatus(`Menu links saved - ${data.declared} item${data.declared === 1 ? '' : 's'}.`, 'success');
      setBusy(false);
      onSaved();
    } catch (e) {
      setProblems([String(e && e.message ? e.message : e)]);
      setStatus('Menu links not saved - network error.', 'error');
      setBusy(false);
    }
  }

  const nudge = { ...S.btn('ghost'), padding: '2px 7px', fontSize: 10, lineHeight: 1.1 };
  const field = { ...S.input, fontSize: 12, color: C.text, padding: '3px 6px' };
  const fieldLabel = { fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.faint2 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 2px' }}>
        <p style={{ fontSize: 11, color: C.faint2, margin: 0, flex: 1 }}>
          Links to your own pages. They have no products behind them, so the shop can
          never work them out on its own and never removes them.
        </p>
        {dirty && (
          <button type="button" style={S.btn('ghost')} disabled={busy}
            onClick={() => { setDraft(storedItems.map((d) => ({ ...d }))); touched(); }}>
            Revert
          </button>
        )}
        <button type="button" style={S.btn('save')} disabled={!dirty || busy} onClick={save}>
          {busy ? 'Saving...' : 'Save menu links'}
        </button>
      </div>

      {problems.length > 0 && (
        <div style={{ padding: '10px 12px', border: `1px solid ${C.line}`, margin: '10px 0' }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: C.text, margin: '0 0 6px' }}>Not saved</p>
          {problems.map((f, i) => (<p key={i} style={{ fontSize: 11, color: C.faint, margin: '0 0 3px' }}>{f}</p>))}
          <p style={{ fontSize: 10, color: C.faint2, margin: '6px 0 0' }}>Nothing was written. The menu is unchanged.</p>
        </div>
      )}

      {items.length === 0 ? (
        <p style={{ fontSize: 12, color: C.faint, padding: '8px 2px', margin: '10px 0 0' }}>
          No other menu links. Add one for a page like Our Story or Contact.
        </p>
      ) : (
        <div style={{ border: `1px solid ${C.line2}`, marginTop: 10 }}>
          {items.map((d, i) => (
            <div key={`declared-${i}`} style={{
              display: 'flex', alignItems: 'flex-end', gap: 8,
              padding: '7px 8px', borderBottom: `1px solid ${C.line2}`,
            }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <button type="button" style={nudge} disabled={i === 0 || busy} onClick={() => move(i, -1)} title="Move up">
                  {'\u25B2'}
                </button>
                <button type="button" style={nudge} disabled={i >= items.length - 1 || busy} onClick={() => move(i, 1)} title="Move down">
                  {'\u25BC'}
                </button>
              </span>

              <span style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 120 }}>
                <span style={fieldLabel}>Menu wording</span>
                <input style={field} maxLength={40} value={d.label || ''} disabled={busy}
                  onChange={(e) => patchAt(i, { label: e.target.value })} />
              </span>

              <span style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 140 }}>
                <span style={fieldLabel}>Web address</span>
                <input style={field} maxLength={200} value={d.path || ''} disabled={busy}
                  placeholder="/our-story"
                  onChange={(e) => patchAt(i, { path: e.target.value })} />
              </span>

              <button type="button" style={S.btn('ghost')} disabled={busy} onClick={() => removeAt(i)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <button type="button" style={S.btn('ghost')} disabled={busy} onClick={add}>Add a menu link</button>
        {incomplete > 0 && (
          <span style={{ fontSize: 10, color: C.faint2 }}>
            {incomplete} incomplete - an entry needs both wording and an address, or it is dropped.
          </span>
        )}
        {badPath > 0 && (
          <span style={{ fontSize: 10, color: C.faint2 }}>
            {badPath} address{badPath === 1 ? '' : 'es'} should start with /
          </span>
        )}
      </div>
    </div>
  );
}

// ── Categories ──────────────────────────────────────────────────────────────
// Three jobs, one modal, one open at a time. They were a single scrolling list
// and that was the mistake: arranging a menu, renaming a category and linking a
// page are different tasks with different costs and different saves, and a
// reader could not tell where one ended and the next began.
//
// Each section keeps its own save button. There is deliberately no single Save
// for the modal: "Save structure" writes config, "Rename" writes products, and
// one button standing for both would hide which of the two just happened.
function CategoryManager({ products, activeGido, taxonomy, taxonomyHealth, declared, onClose, onReload, setStatus }) {
  const [section, setSection] = useState('structure');

  const counts = {};
  for (const p of products) {
    const c = p.category || '';
    if (c) counts[c] = (counts[c] || 0) + 1;
  }
  const cats = Object.keys(counts).sort((a, b) => a.localeCompare(b));
  const nodeCount = (taxonomy || []).length;
  const linkCount = (declared || []).length;

  function tab(key, label, count) {
    const open = section === key;
    return (
      <button
        type="button"
        style={{
          ...S.btn('ghost'),
          borderColor: open ? C.text : C.line,
          color: open ? C.text : C.faint2,
        }}
        onClick={() => setSection(open ? null : key)}
      >
        {open ? '\u25BE' : '\u25B8'} {label} ({count})
      </button>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80 }}
      onClick={onClose}>
      <div style={{ background: C.bg2, border: `1px solid ${C.line}`, width: 'min(760px, 94vw)', maxHeight: '82vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.line2}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.faint2 }}>
            Categories
          </span>
          <button type="button" style={S.btn('ghost')} onClick={onClose}>Close</button>
        </div>

        <div style={{ padding: 14 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: section ? 14 : 0 }}>
            {tab('structure', 'Structure', nodeCount)}
            {tab('rename', 'Rename category', cats.length)}
            {tab('links', 'Menu links', linkCount)}
          </div>

          {section === 'structure' && (
            <StructurePanel taxonomy={taxonomy} taxonomyHealth={taxonomyHealth} products={products}
              activeGido={activeGido} setStatus={setStatus} onSaved={onReload} />
          )}

          {section === 'rename' && (
            <RenamePanel products={products} taxonomy={taxonomy}
              activeGido={activeGido} setStatus={setStatus} onSaved={onReload} />
          )}

          {section === 'links' && (
            <DeclaredPanel declared={declared} taxonomy={taxonomy}
              activeGido={activeGido} setStatus={setStatus} onSaved={onReload} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── main ────────────────────────────────────────────────────────────────────
function SmartShopProducts({ gido, onBack }) {
  // The live GIDO comes from the logged-in user's registry record (App.js -> gido prop).
  // HARD GUARD: never read/write without a real GIDO. A missing gido must NOT fall through
  // to a param-less list-products call (that silently returns Glen's live catalogue).
  const activeGido = gido?.gido_id || null;
  const [products, setProducts] = useState([]);
  // The declared half of the menu - links to the tenant's own pages, which
  // reference no catalogue value and which nothing can regenerate (clause 8).
  const [declared, setDeclared] = useState([]);
  // The RAW taxonomy, straight from list-catalogue: every node, including the ones
  // the owner deliberately hid and the ones with nothing in them yet. NOT the
  // resolved tree the storefront receives - an owner editing her structure needs to
  // see exactly what a visitor must not (D-132).
  const [taxonomy, setTaxonomy] = useState([]);
  const [taxonomyHealth, setTaxonomyHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('products'); // products | new
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showCats, setShowCats] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [status, setStatusState] = useState({ msg: 'Ready', kind: '' });

  const setStatus = (msg, kind = '') => setStatusState({ msg, kind });

  async function load() {
    if (!activeGido) {
      setLoading(false);
      setStatus('No shop identity for this account - cannot load products.', 'error');
      return; // hard stop - never call list-products without an explicit gido
    }
    setLoading(true);
    try {
      // POST with the portal token through authFetch - the same path saveProduct uses,
      // not a second way of getting a token. The old cache: 'no-store' is gone with the
      // GET: a POST is never served from the browser's HTTP cache, so the staleness that
      // workaround existed for cannot happen here.
      const res = await authFetch(CATALOGUE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gido_id: activeGido }),   // D-084 - verified server-side
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setStatus(`Could not load products: ${data.reason || res.status}`, 'error');
        setLoading(false);
        return;
      }
      // SHOP records only, once, here - so every surface on this page inherits it:
      // the table, the header count, the category filter and the Categories panel.
      // 312 of GIDO_IE_0001's catalogue records belong to the SmartData review
      // pipeline (flagged_duplicate, kept_for_later, linked, rejected). That
      // pipeline has no surface in the portal and must not borrow this one - an
      // owner was being shown 261 records he does not own, each with an Edit
      // button writing through the shop's own product writer.
      const list = (data.products || []).filter((p) => SHOP_STATUSES.has(p.status || 'active'));
      setProducts(list);
      // validate() travels with the taxonomy so the manager can show the structural
      // faults resolve() drops silently - an orphan, a duplicate id, a self-parent.
      // That silence is only defensible because this surface breaks it (D-134).
      setTaxonomy(data.taxonomy || []);
      setTaxonomyHealth(data.taxonomy_health || null);
      setDeclared(data.declared || []);
      const archivedCount = list.filter((p) => p.status === 'archived').length;
      // skipped[] is the D-009 amendment's honest half: a record the server could not
      // read costs that one record, and the owner is TOLD. Glen's 51 migration-debris
      // rows land here. Silence would mean a product missing from her own list with
      // nothing on screen to explain it.
      const skipped = (data.skipped || []).length;
      const parts = [`${list.length - archivedCount} products loaded`];
      if (archivedCount) parts.push(`${archivedCount} archived`);
      if (skipped) parts.push(`${skipped} could not be read`);
      setStatus(parts.join(', '), skipped ? 'error' : '');
    } catch (e) {
      setStatus('Could not load products', 'error');
    }
    setLoading(false);
  }
  useEffect(() => { load(); }, [activeGido]);

  const categories = deriveCategories(products);

  // Archived products always come back from the server - a CMS that cannot see what it
  // archived has a one-way door, which is the bug this change exists to fix. They are
  // hidden by default because a 259-product list should not grow by every retired item,
  // the toggle carries the count so nothing hides behind a control she has to find, and
  // when it is on they sort to the TOP because they are the reason she switched it on.
  const archivedTotal = products.filter((p) => p.status === 'archived').length;

  const filtered = products
    .filter((p) =>
      (!search || (p.name || '').toLowerCase().includes(search.toLowerCase())) &&
      (!catFilter || p.category === catFilter) &&
      (showArchived || p.status !== 'archived')
    )
    // Archived first, not last. They are only in the list because the owner clicked
    // "Show archived", so they are what she is looking for - putting them after 250
    // live products means scrolling past everything to reach the thing she asked for.
    // sort() is stable, so the live products keep their existing order underneath.
    .sort((a, b) => Number(b.status === 'archived') - Number(a.status === 'archived'));

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <BackButton onClick={onBack} />
          <h1 style={S.h1}>SmartShop — Products</h1>
        </div>
        <span style={{ fontSize: 10, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {activeGido || 'no shop'} · {products.length} products
          {taxonomy.length ? ` · ${taxonomy.length} structure nodes` : ''}
          {taxonomyHealth && !taxonomyHealth.ok
            ? <span style={{ color: C.accent }}> · structure has faults</span>
            : null}
        </span>
      </header>

      <div style={S.tabs}>
        <button style={S.tab(tab === 'products')} onClick={() => setTab('products')}>Existing Products</button>
        <button style={S.tab(tab === 'new')} onClick={() => { setTab('new'); setExpandedId(null); }}>+ Add New Product</button>
      </div>

      {showCats && (
        <CategoryManager products={products} taxonomy={taxonomy} taxonomyHealth={taxonomyHealth} declared={declared} activeGido={activeGido} setStatus={setStatus}
          onClose={() => setShowCats(false)}
          onReload={load} />
      )}

      {tab === 'products' && (
        <>
          <div style={S.toolbar}>
            <input style={S.search} placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <select style={S.select} value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
              <option value="">All Categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {archivedTotal > 0 && (
              <button type="button" onClick={() => setShowArchived((v) => !v)}
                title="Archived products are removed from your shop but never deleted"
                style={{
                  background: showArchived ? 'rgba(192,39,45,0.15)' : 'none',
                  border: `1px solid ${showArchived ? 'rgba(192,39,45,0.4)' : 'rgba(255,255,255,0.15)'}`,
                  color: showArchived ? '#d8737a' : C.faint,
                  fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em',
                  fontWeight: 700, padding: '7px 12px', cursor: 'pointer', flexShrink: 0,
                }}>
                {showArchived ? `Hide archived (${archivedTotal})` : `Show archived (${archivedTotal})`}
              </button>
            )}
            <button type="button" style={{ ...S.btn('ghost'), padding: '8px 14px', fontSize: 9 }}
              onClick={() => setShowCats(true)}>Categories</button>
            <span style={S.countBadge}>{filtered.length} products</span>
          </div>
          <div style={S.content}>
            {loading && <p style={{ color: C.faint, fontSize: 11 }}>Loading…</p>}
            {!loading && filtered.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: C.faint, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                No products yet for {activeGido}. Add one.
              </div>
            )}
            {filtered.map((p) => (
              <ProductRow
                key={p.product_id}
                p={p}
                expanded={expandedId === p.product_id}
                onToggle={() => setExpandedId(expandedId === p.product_id ? null : p.product_id)}
                onSaved={() => { setExpandedId(null); load(); }}
                setStatus={setStatus}
                activeGido={activeGido}
                categories={categories}
              />
            ))}
          </div>
        </>
      )}

      {tab === 'new' && (
        <div style={S.content}>
          <ProductForm
            initial={emptyForm()}
            onCancel={() => setTab('products')}
            onSaved={() => { setTab('products'); load(); }}
            setStatus={setStatus}
            activeGido={activeGido}
            categories={categories}
          />
        </div>
      )}

      <div style={S.statusBar(status.kind)}>{status.msg}</div>
    </div>
  );
}

export default SmartShopProducts;
